import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell, PieChart, Pie, LabelList,
} from 'recharts';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const fmt = n => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(n || 0);
const toISO = d => d.toISOString().split('T')[0];

const PIE_COLORS = ['#2563eb', '#16a34a', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4'];

function DarkTip({ active, payload, label, currency }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#1e293b', borderRadius: 10, padding: '8px 14px' }}>
      {label && <p style={{ margin: '0 0 4px', fontSize: '0.7rem', color: '#94a3b8' }}>{label}</p>}
      {payload.map((p, i) => (
        <div key={i} style={{ display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '0.75rem', color: p.color }}>{p.name}</span>
          <strong style={{ fontSize: '0.82rem', color: '#f1f5f9' }}>{currency ? fmt(p.value) : p.value}</strong>
        </div>
      ))}
    </div>
  );
}

function ProgBar({ value, max, color = '#2563eb' }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div style={{ height: 6, background: '#f1f5f9', borderRadius: 99, flex: 1 }}>
      <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 99, transition: 'width .5s' }} />
    </div>
  );
}

const Card = ({ children, style }) => (
  <div style={{ background: '#fff', border: '1px solid #e8edf3', borderRadius: 14, padding: '20px', ...style }}>
    {children}
  </div>
);

const SecTitle = ({ title, sub }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{ fontWeight: 600, fontSize: '0.92rem', color: '#0f172a' }}>{title}</div>
    {sub && <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 2 }}>{sub}</div>}
  </div>
);

const Empty = () => (
  <div style={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '0.85rem' }}>
    Sin datos para este período
  </div>
);

const BarTop = ({ x, y, width, value }) =>
  value ? <text x={x + width / 2} y={y - 5} textAnchor="middle" fontSize={10} fontWeight={600} fill="#64748b">{value}</text> : null;

export default function AdvancedReportsPage() {
  const { hasPlan } = useAuth();
  const [tab, setTab]       = useState('trends');
  const [from, setFrom]     = useState(toISO(new Date(new Date().getFullYear(), new Date().getMonth() - 2, 1)));
  const [to, setTo]         = useState(toISO(new Date()));
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(tab === 'trends' ? '/reports/trends' : '/reports/profitability', { params: { from, to } })
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [tab, from, to]);

  useEffect(() => { load(); }, [load]);

  if (!hasPlan('has_advanced_reports')) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon">📈</div>
        <h3>Módulo no disponible</h3>
        <p>Los reportes avanzados están disponibles en el <strong>Plan Medio</strong> o superior.</p>
      </div>
    );
  }

  /* ── Derivados ─────────────────────────────────────────────────────────── */
  const byHour      = data?.by_hour      || [];
  const byWorker    = data?.by_worker    || [];
  const monthly     = data?.monthly_growth || [];
  const topServices = data?.top_services || [];
  const maxHour     = Math.max(...byHour.map(r => r.total || 0), 1);
  const topSvcMax   = Math.max(...topServices.map(r => r.total || 0), 1);

  const byBranch    = data?.by_branch           || [];
  const byBranchExp = data?.expenses_by_branch  || [];
  const branchCombo = byBranch.map(b => {
    const gastos = byBranchExp.find(g => g.sede === b.sede)?.gastos || 0;
    return { sede: b.sede, Ingresos: b.ingresos || 0, Gastos: gastos, Utilidad: (b.ingresos || 0) - gastos };
  });
  const totalIngresos = branchCombo.reduce((s, b) => s + b.Ingresos, 0) || data?.total_income || 0;
  const totalGastos   = branchCombo.reduce((s, b) => s + b.Gastos,   0);
  const totalUtilidad = totalIngresos - totalGastos;

  return (
    <>
      {/* ── Encabezado ──────────────────────────────────────────────────────── */}
      <div className="page-header">
        <div className="page-header-text">
          <h2>Reportes Avanzados</h2>
          <p>Análisis profundo de tendencias y rentabilidad</p>
        </div>
      </div>

      {/* ── Filtros ─────────────────────────────────────────────────────────── */}
      <Card style={{ marginBottom: 18 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Desde</label>
            <input type="date" className="form-input" value={from} onChange={e => setFrom(e.target.value)} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Hasta</label>
            <input type="date" className="form-input" value={to} onChange={e => setTo(e.target.value)} />
          </div>
          <button className="btn btn-primary" onClick={load}>Aplicar</button>
        </div>
      </Card>

      {/* ── Tabs ────────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 18, borderBottom: '1px solid #e8edf3' }}>
        {[
          { key: 'trends',       label: '📈 Tendencias'   },
          { key: 'profitability', label: '💵 Rentabilidad' },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{
              padding: '10px 18px', background: 'none', border: 'none', cursor: 'pointer',
              fontSize: '0.875rem', fontWeight: tab === t.key ? 600 : 400,
              color: tab === t.key ? '#2563eb' : '#64748b',
              borderBottom: tab === t.key ? '2px solid #2563eb' : '2px solid transparent',
              marginBottom: -1, transition: 'color .15s',
            }}>{t.label}</button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 64 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
      ) : !data ? (
        <div className="empty-state"><div className="empty-state-icon">📊</div><h3>Sin datos para el período</h3></div>

      /* ════════════ TENDENCIAS ════════════ */
      ) : tab === 'trends' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Horarios más ocupados */}
          <Card>
            <SecTitle title="Horarios más ocupados" sub="Distribución de citas por hora del día" />
            {byHour.length === 0 ? <Empty /> : (
              <>
                <ResponsiveContainer width="100%" height={210}>
                  <BarChart data={byHour.map(r => ({ hora: `${String(r.hora).padStart(2,'0')}:00`, Total: r.total, Atendidas: r.atendidas }))}
                    margin={{ top: 20, right: 8, left: -24, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="hora" tick={{ fill: '#94a3b8', fontSize: 10 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip content={<DarkTip />} cursor={{ fill: '#f8fafc' }} />
                    <Legend wrapperStyle={{ fontSize: 11, color: '#64748b' }} />
                    <Bar dataKey="Total"     fill="#e2e8f0" radius={[4,4,0,0]} maxBarSize={20} />
                    <Bar dataKey="Atendidas" fill="#2563eb" radius={[4,4,0,0]} maxBarSize={20}>
                      <LabelList dataKey="Atendidas" content={BarTop} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>

                {/* Fila resumen compacta */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 14, paddingTop: 14, borderTop: '1px solid #f1f5f9' }}>
                  {byHour.map((r, i) => {
                    const pct = r.total > 0 ? Math.round(r.atendidas * 100 / r.total) : 0;
                    const barCol = pct > 70 ? '#16a34a' : pct > 40 ? '#f59e0b' : '#94a3b8';
                    return (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 160, flex: '1 1 160px' }}>
                        <span style={{ fontSize: '0.75rem', color: '#475569', minWidth: 42, fontWeight: 600 }}>{String(r.hora).padStart(2,'0')}:00</span>
                        <ProgBar value={r.total} max={maxHour} color={barCol} />
                        <span style={{ fontSize: '0.72rem', color: '#64748b', minWidth: 18, textAlign: 'right' }}>{r.total}</span>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </Card>

          {/* Crecimiento mensual + Top servicios */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 14 }}>

            <Card>
              <SecTitle title="Crecimiento mensual" sub="Evolución de citas mes a mes" />
              {monthly.length === 0 ? <Empty /> : (
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={monthly.map(r => ({ mes: r.mes, Total: r.total, Atendidas: r.atendidas }))}
                    margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gT" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor="#2563eb" stopOpacity={0.2}/>
                        <stop offset="95%" stopColor="#2563eb" stopOpacity={0}  />
                      </linearGradient>
                      <linearGradient id="gA" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor="#16a34a" stopOpacity={0.2}/>
                        <stop offset="95%" stopColor="#16a34a" stopOpacity={0}  />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="mes" tick={{ fill: '#94a3b8', fontSize: 10 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip content={<DarkTip />} />
                    <Legend wrapperStyle={{ fontSize: 11, color: '#64748b' }} />
                    <Area type="monotone" dataKey="Total"     stroke="#2563eb" fill="url(#gT)" strokeWidth={2} dot={{ r: 4, fill: '#2563eb'  }} />
                    <Area type="monotone" dataKey="Atendidas" stroke="#16a34a" fill="url(#gA)" strokeWidth={2} dot={{ r: 4, fill: '#16a34a' }} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </Card>

            <Card>
              <SecTitle title="Top 5 servicios más demandados" />
              {topServices.length === 0 ? <Empty /> : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {topServices.map((r, i) => {
                    const pct = Math.round(r.total * 100 / topSvcMax);
                    return (
                      <div key={i}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                          <span style={{ fontSize: '0.84rem', fontWeight: 600, color: '#1e293b' }}>
                            {['🥇','🥈','🥉','4️⃣','5️⃣'][i]} {r.nombre}
                          </span>
                          <span style={{ fontSize: '0.8rem', color: '#b45309', fontWeight: 700 }}>{fmt(r.ingresos)}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <ProgBar value={r.total} max={topSvcMax} color={PIE_COLORS[i] || '#2563eb'} />
                          <span style={{ fontSize: '0.72rem', color: '#64748b', minWidth: 52, textAlign: 'right' }}>{r.total} veces</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          </div>

          {/* Ocupación por barbero */}
          <Card>
            <SecTitle title="Ocupación por barbero" sub="Citas atendidas e ingresos generados en el período" />
            {byWorker.length === 0 ? <Empty /> : (
              <>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={byWorker.map(r => ({ barbero: r.barbero, 'Total citas': r.total_citas, Atendidas: r.atendidas }))}
                    margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="barbero" tick={{ fill: '#475569', fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip content={<DarkTip />} cursor={{ fill: '#f8fafc' }} />
                    <Legend wrapperStyle={{ fontSize: 11, color: '#64748b' }} />
                    <Bar dataKey="Total citas" fill="#e2e8f0" radius={[4,4,0,0]} maxBarSize={24} />
                    <Bar dataKey="Atendidas"   fill="#2563eb" radius={[4,4,0,0]} maxBarSize={24} />
                  </BarChart>
                </ResponsiveContainer>

                <div className="table-wrapper" style={{ marginTop: 16 }}>
                  <table>
                    <thead>
                      <tr><th>Barbero</th><th>Total citas</th><th>Atendidas</th><th>Eficiencia</th><th>Ingresos</th></tr>
                    </thead>
                    <tbody>
                      {byWorker.map((r, i) => {
                        const pct = r.total_citas > 0 ? Math.round(r.atendidas * 100 / r.total_citas) : 0;
                        const col = pct >= 80 ? '#16a34a' : pct >= 50 ? '#f59e0b' : '#ef4444';
                        return (
                          <tr key={i}>
                            <td style={{ fontWeight: 600 }}>{r.barbero}</td>
                            <td>{r.total_citas}</td>
                            <td style={{ color: '#16a34a', fontWeight: 600 }}>{r.atendidas}</td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <ProgBar value={pct} max={100} color={col} />
                                <span style={{ fontSize: '0.78rem', minWidth: 34, color: col, fontWeight: 700 }}>{pct}%</span>
                              </div>
                            </td>
                            <td style={{ color: '#b45309', fontWeight: 600 }}>{fmt(r.ingresos)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </Card>
        </div>

      /* ════════════ RENTABILIDAD ════════════ */
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* KPIs rentabilidad */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px,1fr))', gap: 12 }}>
            {[
              { icon: '💵', bg: '#fef9c3', label: 'Ingresos totales', value: fmt(totalIngresos), color: '#b45309' },
              { icon: '📉', bg: '#fee2e2', label: 'Gastos totales',   value: fmt(totalGastos),   color: '#b91c1c' },
              { icon: '📊', bg: totalUtilidad >= 0 ? '#dcfce7' : '#fee2e2',
                label: 'Utilidad neta', value: fmt(totalUtilidad), color: totalUtilidad >= 0 ? '#15803d' : '#b91c1c' },
            ].map((k, i) => (
              <div key={i} style={{ background: '#fff', border: '1px solid #e8edf3', borderRadius: 14, padding: '16px 18px', display: 'flex', alignItems: 'flex-start', gap: 13 }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: k.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', flexShrink: 0 }}>{k.icon}</div>
                <div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 700, color: k.color }}>{k.value}</div>
                  <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: 2 }}>{k.label}</div>
                </div>
              </div>
            ))}
          </div>

          {branchCombo.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">💵</div>
              <h3>Sin datos de rentabilidad</h3>
              <p>Configura sucursales y registra gastos para ver el análisis comparativo.</p>
            </div>
          ) : (
            <>
              {/* Barras agrupadas por sucursal */}
              <Card>
                <SecTitle title="Ingresos vs Gastos vs Utilidad por sucursal" />
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={branchCombo} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="sede" tick={{ fill: '#475569', fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} axisLine={false} tickLine={false}
                      tickFormatter={v => v >= 1000000 ? `$${(v/1000000).toFixed(1)}M` : `$${(v/1000).toFixed(0)}K`} />
                    <Tooltip content={<DarkTip currency />} cursor={{ fill: '#f8fafc' }} />
                    <Legend wrapperStyle={{ fontSize: 11, color: '#64748b' }} />
                    <Bar dataKey="Ingresos"  fill="#2563eb" radius={[4,4,0,0]} maxBarSize={26} />
                    <Bar dataKey="Gastos"    fill="#ef4444" radius={[4,4,0,0]} maxBarSize={26} />
                    <Bar dataKey="Utilidad"  fill="#16a34a" radius={[4,4,0,0]} maxBarSize={26} />
                  </BarChart>
                </ResponsiveContainer>
              </Card>

              {/* Dona + tabla detalle */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px,1fr))', gap: 14 }}>
                <Card>
                  <SecTitle title="Participación de ingresos por sucursal" />
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie data={branchCombo.map((b,i) => ({ name: b.sede, value: b.Ingresos }))}
                        cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={3} dataKey="value">
                        {branchCombo.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={v => [fmt(v), 'Ingresos']}
                        contentStyle={{ background: '#1e293b', border: 'none', borderRadius: 10 }}
                        labelStyle={{ color: '#94a3b8' }} itemStyle={{ color: '#f1f5f9' }} />
                      <Legend wrapperStyle={{ fontSize: 11, color: '#64748b' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </Card>

                <Card>
                  <SecTitle title="Detalle por sucursal" />
                  <div className="table-wrapper">
                    <table>
                      <thead><tr><th>Sucursal</th><th>Ingresos</th><th>Gastos</th><th>Utilidad</th></tr></thead>
                      <tbody>
                        {branchCombo.map((r, i) => (
                          <tr key={i}>
                            <td style={{ fontWeight: 600 }}>{r.sede}</td>
                            <td style={{ color: '#1d4ed8', fontWeight: 600 }}>{fmt(r.Ingresos)}</td>
                            <td style={{ color: '#b91c1c'                  }}>{fmt(r.Gastos)}</td>
                            <td style={{ color: r.Utilidad >= 0 ? '#15803d' : '#b91c1c', fontWeight: 700 }}>{fmt(r.Utilidad)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
